import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Search, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { FilterSelect } from '../findings/filters/FilterSelect';
import { CredentialsApi, Source } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';

interface PublicLeaksFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedSource: string;
  onSourceChange: (source: string) => void;
  onReset: () => void;
}

export const PublicLeaksFilters: React.FC<PublicLeaksFiltersProps> = ({
  searchQuery,
  onSearchChange,
  selectedSource,
  onSourceChange,
  onReset,
}) => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [sources, setSources] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSources = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.sourcesList({
        teamSlug: currentTeam.slug,
        pageSize: 1000,
      });

      const sourceOptions = response.results
        .filter(source => source.name && source.name.trim() !== '')
        .map((source: Source) => ({
          value: source.name,
          label: source.name,
        }));
      setSources([{ value: 'all', label: 'All Sources' }, ...sourceOptions]);
    } catch (err) {
      console.error('Error fetching sources:', err);
    }
  }, [currentTeam?.slug, token]);

  useEffect(() => {
    setIsLoading(true);
    fetchSources().finally(() => {
      setIsLoading(false);
    });
  }, [fetchSources]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Search public leaks..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-48">
            <FilterSelect
              value={selectedSource}
              onValueChange={onSourceChange}
              options={sources}
              placeholder="All Sources"
              label="Source"
              scrollable={true}
              searchable={true}
            />
          </div>

          <Button
            variant="outline"
            onClick={onReset}
            className="flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Reset</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
